<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h2>Raccolta Dati</h2>
      </v-col>
    </v-row>

    <!-- Selezione Paziente + Skill -->
    <v-row>
      <v-col cols="12" md="4">
        <v-autocomplete
          v-model="pazienteId"
          :items="allPazienti"
          item-text="fullName"
          item-value="id"
          label="Paziente"
          outlined
          dense
          clearable
          prepend-icon="mdi-account"
          @change="onPatientChange"
        />
      </v-col>

      <v-col cols="12" md="8">
        <v-autocomplete
          v-model="pazienteSkillId"
          :items="skillOptions"
          item-text="label"
          item-value="id"
          label="Skill"
          outlined
          dense
          clearable
          :disabled="!pazienteId"
          prepend-icon="mdi-format-list-checks"
          :loading="loadingSkills"
          @change="onSkillChange"
        />
      </v-col>
    </v-row>

    <!-- Gate / Stato -->
    <v-row v-if="gate">
      <v-col cols="12">
        <v-alert
          :type="gate.canCollectData ? 'info' : 'warning'"
          outlined
          dense
        >
          {{ gateMessage }}
        </v-alert>
      </v-col>
    </v-row>

    <!-- PROBE / CONT UI -->
    <v-row v-if="gate && gate.canCollectData && pazienteSkillId">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="d-flex flex-wrap align-center justify-space-between">
            <div class="d-flex flex-wrap align-center">
              <div class="mr-3 font-weight-medium">{{ selectedSkillLabel }}</div>
              <v-chip small outlined>{{ dataCollectionType || '-' }}</v-chip>
            </div>
            <div class="d-flex align-center mt-2 mt-md-0">
              <v-chip v-if="dataCollectionType === 'CONT'" small outlined :color="contTargetReached ? 'orange' : undefined" class="mr-2">
                {{ contTargetLabel }}
              </v-chip>
              <v-btn
                small
                outlined
                color="grey darken-1"
                :disabled="!lastSavedEvent || undoingEvent"
                :loading="undoingEvent"
                @click="undoLastEvent"
              >
                <v-icon left small>mdi-undo</v-icon>
                Undo
              </v-btn>
            </div>
          </v-card-title>

          <v-card-text>
            <!-- PROBE -->
            <div v-if="dataCollectionType === 'PROBE'">
              <div class="capture-buttons">
                <v-btn
                  x-large
                  depressed
                  color="success"
                  class="capture-button"
                  :outlined="probeValue !== true"
                  :disabled="postingProbe"
                  @click="sendProbe(true)"
                >
                  <v-icon left>mdi-check</v-icon>
                  Y
                </v-btn>

                <v-btn
                  x-large
                  depressed
                  color="error"
                  class="capture-button"
                  :outlined="probeValue !== false"
                  :disabled="postingProbe"
                  @click="sendProbe(false)"
                >
                  <v-icon left>mdi-close</v-icon>
                  N
                </v-btn>

                <v-progress-circular
                  v-if="postingProbe"
                  indeterminate
                  size="20"
                />
              </div>
            </div>

            <!-- CONT -->
            <div v-else-if="dataCollectionType === 'CONT'">
              <div class="capture-buttons">
                <v-btn
                  x-large
                  depressed
                  color="success"
                  class="capture-button"
                  :disabled="postingCont || contTargetReached"
                  @click="incCorrect"
                >
                  <v-icon left>mdi-check</v-icon>
                  +1
                </v-btn>

                <v-btn
                  x-large
                  depressed
                  color="error"
                  class="capture-button"
                  :disabled="postingCont || contTargetReached"
                  @click="incError"
                >
                  <v-icon left>mdi-close</v-icon>
                  +1
                </v-btn>

                <div class="d-flex align-center cont-counter">
                  <v-icon color="green" class="mr-2">mdi-check</v-icon>
                  <span class="mr-4">{{ contCorrect }}</span>
                  <v-icon color="red" class="mr-2">mdi-close</v-icon>
                  <span>{{ contError }}</span>
                </div>

                <v-progress-circular
                  v-if="postingCont"
                  class="ml-4"
                  indeterminate
                  size="20"
                />
              </div>

              <v-alert v-if="contTargetReached" type="success" outlined dense class="mt-3 mb-0">
                Target completato.
              </v-alert>
            </div>

            <!-- Altro -->
            <div v-else>
              <v-alert type="info" outlined dense>
                Tipo raccolta dati non disponibile
              </v-alert>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- GRAFICO MINIMALE -->
    <v-row v-if="pazienteSkillId">
      <v-col cols="12">
        <v-card outlined class="mt-4">
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-chart-line</v-icon>
            Andamento
            <v-spacer></v-spacer>

            <v-select
              v-model="historyDays"
              :items="historyDayOptions"
              dense
              hide-details
              outlined
              class="history-range-select mr-2"
              @change="loadHistory"
            />

            <v-btn icon :loading="loadingHistory" @click="loadHistory">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-progress-linear v-if="loadingHistory" indeterminate class="mb-3" />

            <v-alert v-if="!historyHasData && !loadingHistory" type="info" outlined dense>
              Nessun dato nel periodo selezionato.
            </v-alert>

            <div v-else-if="historyHasData">
              <div class="d-flex flex-wrap mb-3">
                <v-chip small outlined class="mr-2 mb-2">
                  Media {{ historyAverageLabel }}
                </v-chip>
                <v-chip small outlined color="green" class="mr-2 mb-2">
                  Corretti {{ historyTotalCorrect }}
                </v-chip>
                <v-chip small outlined color="red" class="mr-2 mb-2">
                  Errori {{ historyTotalError }}
                </v-chip>
                <v-chip small outlined class="mb-2">
                  Totale {{ historyTotalTrials }}
                </v-chip>
              </div>

              <div class="aba-chart">
                <svg viewBox="0 0 320 160" role="img">
                  <line x1="26" y1="12" x2="306" y2="12" class="chart-grid" />
                  <line x1="26" y1="72" x2="306" y2="72" class="chart-grid" />
                  <line x1="26" y1="132" x2="306" y2="132" class="chart-axis" />
                  <line x1="26" y1="12" x2="26" y2="132" class="chart-axis" />

                  <text x="3" y="16" class="chart-label">100</text>
                  <text x="8" y="75" class="chart-label">50</text>
                  <text x="14" y="135" class="chart-label">0</text>

                  <polyline
                    v-if="chartPolyline"
                    :points="chartPolyline"
                    class="chart-line"
                  />

                  <circle
                    v-for="point in chartPoints"
                    :key="point.key"
                    :cx="point.x"
                    :cy="point.y"
                    r="4"
                    class="chart-point"
                  />
                </svg>
              </div>

              <v-simple-table dense class="mt-3">
                <tbody>
                  <tr v-for="row in historyRecentRows" :key="row.day">
                    <td>{{ formatShortDate(row.day) }}</td>
                    <td>{{ percentLabel(row.percent) }}</td>
                    <td class="text-right">
                      {{ row.correct }} / {{ row.total }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import axios from '@/api/axios';

export default {
  data() {
    return {
      pazienteId: null,
      pazienteSkillId: null,

      skillsRaw: [],
      gate: null,
      dataCollectionType: null,

      // PROBE
      probeValue: null,
      postingProbe: false,

      // CONT (contatori locali + refresh su today)
      contCorrect: 0,
      contError: 0,
      postingCont: false,
      lastSavedEvent: null,
      undoingEvent: false,

      loadingSkills: false,
      loadingGate: false,
      loadingHistory: false,

      historyDays: 30,
      historyDayOptions: [
        { text: '7 giorni', value: 7 },
        { text: '14 giorni', value: 14 },
        { text: '30 giorni', value: 30 },
        { text: '60 giorni', value: 60 }
      ],
      historyChart: null,
    };
  },

  computed: {
    ...mapGetters('paziente', ['allPazienti']),

    skillOptions() {
      return (this.skillsRaw || []).map(s => ({
        ...s,
        label: this.skillLabel(s)
      }));
    },

    selectedSkillLabel() {
      const found = (this.skillOptions || []).find(x => x.id === this.pazienteSkillId);
      return found ? found.label : '';
    },

    gateMessage() {
      // backend potrebbe mandare message o reason: gestiamo entrambi
      if (!this.gate) return '';
      return this.gate.message || this.gate.reason || (this.gate.canCollectData ? 'Raccolta dati abilitata' : 'Raccolta dati bloccata');
    },

    statusLabel() {
      // se lo stato arriva nel gate (status / pazienteSkillStatus ecc.)
      if (!this.gate) return '';
      return this.gate.status || this.gate.pazienteSkillStatus || '';
    },

    trialsTarget() {
      return Number(this.gate && this.gate.trialsTarget ? this.gate.trialsTarget : 0);
    },

    contTotalToday() {
      return this.contCorrect + this.contError;
    },

    contTargetReached() {
      return this.dataCollectionType === 'CONT' && this.trialsTarget > 0 && this.contTotalToday >= this.trialsTarget;
    },

    contTargetLabel() {
      return this.trialsTarget > 0 ? `${this.contTotalToday}/${this.trialsTarget}` : `${this.contTotalToday}`;
    },

    historySeries() {
      return this.historyChart && Array.isArray(this.historyChart.series)
        ? this.historyChart.series[0]
        : null;
    },

    historyDaily() {
      const points = this.historySeries && Array.isArray(this.historySeries.points)
        ? this.historySeries.points
        : [];

      return points.map(point => ({
        day: point.bucket,
        correct: point.correct || 0,
        error: point.error || 0,
        total: point.total || 0,
        percent: point.percent === null || point.percent === undefined ? null : point.percent
      }));
    },

    historyHasData() {
      return this.historyDaily.some(row => row.total > 0);
    },

    historyTotalCorrect() {
      return this.historyDaily.reduce((sum, row) => sum + row.correct, 0);
    },

    historyTotalError() {
      return this.historyDaily.reduce((sum, row) => sum + row.error, 0);
    },

    historyTotalTrials() {
      return this.historyTotalCorrect + this.historyTotalError;
    },

    historyAverageLabel() {
      if (!this.historyTotalTrials) return '-';
      return `${Math.round((this.historyTotalCorrect / this.historyTotalTrials) * 100)}%`;
    },

    chartPoints() {
      const width = 280;
      const left = 26;
      const top = 12;
      const height = 120;
      const denominator = Math.max(this.historyDaily.length - 1, 1);

      return this.historyDaily
        .map((row, index) => {
          if (row.total <= 0 || row.percent === null) return null;
          const x = left + (index / denominator) * width;
          const y = top + (1 - row.percent / 100) * height;
          return {
            key: row.day,
            x: Number(x.toFixed(1)),
            y: Number(y.toFixed(1))
          };
        })
        .filter(Boolean);
    },

    chartPolyline() {
      return this.chartPoints.map(p => `${p.x},${p.y}`).join(' ');
    },

    historyRecentRows() {
      return this.historyDaily
        .filter(row => row.total > 0)
        .slice(-6)
        .reverse();
    }
  },

  async mounted() {
    // stesso pattern di SkillsTracking.vue
    await this.fetchPazienti();
  },

  methods: {
    ...mapActions('paziente', ['fetchPazienti']),

    onPatientChange() {
      // reset a cascata
      this.pazienteSkillId = null;
      this.skillsRaw = [];
      this.gate = null;
      this.dataCollectionType = null;
      this.probeValue = null;
      this.contCorrect = 0;
      this.contError = 0;
      this.historyChart = null;
      this.lastSavedEvent = null;

      if (this.pazienteId) this.loadSkills();
    },

    onSkillChange() {
      this.gate = null;
      this.dataCollectionType = null;
      this.probeValue = null;
      this.contCorrect = 0;
      this.contError = 0;
      this.historyChart = null;
      this.lastSavedEvent = null;

      if (this.pazienteSkillId) this.loadStateAndMaybeToday();
    },

    skillLabel(s) {
      // super-robusta per evitare [object Object]
      const si = s && (s.skillItem || s.skill || s.item);
      const name =
        (s && (s.skillItemName || s.skillName || s.nome || s.name)) ||
        (si && (si.name || si.nome || si.description)) ||
        '';

      const area =
        (s && (s.skillAreaName || s.areaName)) ||
        (si && (si.areaName || (si.skillArea && si.skillArea.name))) ||
        '';

      // opzionale: tipo raccolta dati se già incluso nel dto
      const t = s && (s.dataCollectionType || (s.state && s.state.dataCollectionType));

      const base = area ? `${area} - ${name}` : name;
      return t ? `${base} (${t})` : base;
    },

    async loadSkills() {
      this.loadingSkills = true;
      try {
        const res = await axios.get(`/paziente-skills/by-paziente/${this.pazienteId}`);
        this.skillsRaw = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.handleHttpError(e, 'Errore caricamento skill');
      } finally {
        this.loadingSkills = false;
      }
    },

    async loadStateAndMaybeToday() {
      this.loadingGate = true;
      try {
        const res = await axios.get(`/paziente-skills/${this.pazienteSkillId}/state`);
        this.gate = res.data;
        this.dataCollectionType = res.data && res.data.dataCollectionType ? res.data.dataCollectionType : null;

        // se CONT proviamo a leggere i dati di oggi per riempire contatori (best-effort)
        if (this.gate && this.gate.canCollectData && this.dataCollectionType === 'CONT') {
          await this.loadTodayCountsBestEffort();
        }
        await this.loadHistory();
      } catch (e) {
        this.handleHttpError(e, 'Errore caricamento stato skill');
      } finally {
        this.loadingGate = false;
      }
    },

    // best-effort: se l’endpoint ritorna eventi con correctCount/errorCount o delta, aggiorniamo
    async loadTodayCountsBestEffort() {
      try {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        const from = `${yyyy}-${mm}-${dd}`;
        const to = this.addDaysISO(from, 1);

        const res = await axios.get('/data-collection/chart', {
          params: {
            pazienteSkillId: this.pazienteSkillId,
            from,
            to,
            aggregation: 'DAY'
          }
        });

        const series = res.data && Array.isArray(res.data.series) ? res.data.series[0] : null;
        const point = series && Array.isArray(series.points) ? series.points[0] : null;
        this.contCorrect = point ? point.correct || 0 : 0;
        this.contError = point ? point.error || 0 : 0;
      } catch (_) {
        // silent: non blocchiamo la UI se il formato è diverso
      }
    },

    async sendProbe(success) {
      if (!this.pazienteSkillId) return;
      this.postingProbe = true;
      try {
        const terapistaId = this.$store.getters['auth/getUser']?.id;
        const day = new Date().toISOString().substr(0, 10);
        const res = await axios.post(`/data-collection/probe/${this.pazienteSkillId}`, { day, result: success, terapistaId });
        this.probeValue = success;
        this.lastSavedEvent = { id: res.data, mode: 'PROBE', result: success };
        await this.loadHistory();
        this.$snackbar && this.$snackbar(success ? 'Registrato: Y' : 'Registrato: N');
      } catch (e) {
        this.handleHttpError(e, 'Errore salvataggio PROBE');
      } finally {
        this.postingProbe = false;
      }
    },

    async incCorrect() {
      if (!this.pazienteSkillId) return;
      if (this.contTargetReached) {
        this.$snackbar && this.$snackbar('Target già completato');
        return;
      }
      this.postingCont = true;
      try {
        const terapistaId = this.$store.getters['auth/getUser']?.id;
        const res = await axios.post(`/data-collection/cont/${this.pazienteSkillId}`, { result: true, terapistaId });
        this.contCorrect += 1;
        this.lastSavedEvent = { id: res.data, mode: 'CONT', result: true };
        await this.loadHistory();
      } catch (e) {
        this.handleHttpError(e, 'Errore salvataggio CONT');
      } finally {
        this.postingCont = false;
      }
    },

    async incError() {
      if (!this.pazienteSkillId) return;
      if (this.contTargetReached) {
        this.$snackbar && this.$snackbar('Target già completato');
        return;
      }
      this.postingCont = true;
      try {
        const terapistaId = this.$store.getters['auth/getUser']?.id;
        const res = await axios.post(`/data-collection/cont/${this.pazienteSkillId}`, { result: false, terapistaId });
        this.contError += 1;
        this.lastSavedEvent = { id: res.data, mode: 'CONT', result: false };
        await this.loadHistory();
      } catch (e) {
        this.handleHttpError(e, 'Errore salvataggio CONT');
      } finally {
        this.postingCont = false;
      }
    },

    async undoLastEvent() {
      if (!this.lastSavedEvent || !this.lastSavedEvent.id) return;
      this.undoingEvent = true;
      try {
        const event = this.lastSavedEvent;
        await axios.delete(`/skill-data-events/${event.id}`);

        if (event.mode === 'CONT') {
          if (event.result === true && this.contCorrect > 0) this.contCorrect -= 1;
          if (event.result === false && this.contError > 0) this.contError -= 1;
        } else if (event.mode === 'PROBE') {
          this.probeValue = null;
        }

        this.lastSavedEvent = null;
        await this.loadHistory();
        this.$snackbar && this.$snackbar('Ultimo dato annullato');
      } catch (e) {
        this.handleHttpError(e, 'Errore undo');
      } finally {
        this.undoingEvent = false;
      }
    },

    async loadHistory() {
      if (!this.pazienteSkillId) {
        this.historyChart = null;
        return;
      }

      this.loadingHistory = true;
      try {
        const today = this.todayISO();
        const from = this.addDaysISO(today, -(this.historyDays - 1));
        const to = this.addDaysISO(today, 1);
        const res = await axios.get('/data-collection/chart', {
          params: {
            pazienteSkillId: this.pazienteSkillId,
            from,
            to,
            aggregation: 'DAY'
          }
        });
        this.historyChart = res.data || null;
      } catch (e) {
        this.historyChart = null;
        this.handleHttpError(e, 'Errore caricamento andamento');
      } finally {
        this.loadingHistory = false;
      }
    },

    todayISO() {
      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },

    addDaysISO(isoDate, days) {
      const d = new Date(`${isoDate}T00:00:00`);
      d.setDate(d.getDate() + days);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },

    datePart(value) {
      if (!value) return null;
      return String(value).substr(0, 10);
    },

    formatShortDate(day) {
      if (!day || day.length < 10) return day || '-';
      return `${day.substr(8, 2)}/${day.substr(5, 2)}`;
    },

    percentLabel(value) {
      return value === null || value === undefined ? '-' : `${value}%`;
    },

    handleHttpError(err, fallbackMsg) {
      const status = err && err.response ? err.response.status : null;
      const data = err && err.response ? err.response.data : null;

      if (status === 409) {
        this.$snackbar && this.$snackbar(typeof data === 'string' ? data : 'Operazione non consentita (409)');
        return;
      }
      if (status === 400) {
        // validation: se arriva stringa o oggetto
        if (typeof data === 'string') this.$snackbar && this.$snackbar(data);
        else if (data && data.message) this.$snackbar && this.$snackbar(data.message);
        else this.$snackbar && this.$snackbar('Dati non validi (400)');
        return;
      }

      // 500 / network / altro
      this.$snackbar && this.$snackbar(fallbackMsg || 'Errore inatteso');
      // opzionale: console per debug
      // console.error(err);
    }
  }
};
</script>

<style scoped>
.history-range-select {
  max-width: 130px;
}

.capture-buttons {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.capture-button {
  min-height: 64px;
  min-width: 128px;
}

.cont-counter {
  font-size: 1.15rem;
  min-height: 64px;
}

.aba-chart {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fff;
  padding: 8px;
}

.aba-chart svg {
  display: block;
  width: 100%;
  height: 180px;
}

.chart-grid {
  stroke: #eceff1;
  stroke-width: 1;
}

.chart-axis {
  stroke: #b0bec5;
  stroke-width: 1.2;
}

.chart-line {
  fill: none;
  stroke: #1976d2;
  stroke-width: 2.4;
  vector-effect: non-scaling-stroke;
}

.chart-point {
  fill: #1976d2;
  stroke: #fff;
  stroke-width: 2;
  vector-effect: non-scaling-stroke;
}

.chart-label {
  fill: #78909c;
  font-size: 9px;
}
</style>
